import { api } from "../api/client";
import type { BuiltTx } from "../api/types";
import { base64ToBytes, bytesToBase64, decodeWireTransaction, feePayerOf, mergeSignatures, requiredSigners, sendSignedTransaction } from "./tx";
import type { GameWallet } from "./types";

export type SubmitRoute = "auto" | "register" | "migrate";

/** Wallet-signs an API-built transaction and returns the signed base64 wire transaction. */
export async function signBuilt(wallet: GameWallet, built: Pick<BuiltTx, "transaction">): Promise<{ signed: Uint8Array; base64: string; feePayer: string }> {
  const unsigned = base64ToBytes(built.transaction);
  const tx = decodeWireTransaction(unsigned);
  if (!requiredSigners(tx).includes(wallet.address)) {
    throw new Error("This transaction isn't for your wallet; refusing to sign it.");
  }
  const signedByWallet = await wallet.signTransaction(unsigned);
  const signers = requiredSigners(tx);
  if (signers.length === 1 && feePayerOf(tx) === wallet.address) {
    // Sole signer and fee payer: wallets such as Phantom may add priority-fee instructions,
    // and no other signature depends on the message, so send what the wallet signed.
    const out = decodeWireTransaction(signedByWallet);
    if (feePayerOf(out) !== wallet.address || !(out.signatures as Record<string, unknown>)[wallet.address]) {
      throw new Error("The wallet returned a transaction for a different payer; refusing to send it.");
    }
    return { signed: signedByWallet, base64: bytesToBase64(signedByWallet), feePayer: wallet.address };
  }
  const signed = mergeSignatures(unsigned, signedByWallet);
  return { signed, base64: bytesToBase64(signed), feePayer: feePayerOf(tx) };
}

/**
 * POST build → wallet sign → submit (§7.7–7.9, WEB-15). Registration and migration have
 * their own submit endpoints. Otherwise a transaction whose fee payer is the player's
 * wallet is sent straight to RPC, and one whose fee payer is Kora (or the GA for a first
 * claim) goes through `/relay/sign-and-send`.
 */
export async function signAndSubmit(wallet: GameWallet, built: Pick<BuiltTx, "transaction">, route: SubmitRoute = "auto"): Promise<string> {
  const { signed, base64, feePayer } = await signBuilt(wallet, built);
  switch (route) {
    case "register":
      return (await api.registerSubmit(base64)).signature;
    case "migrate":
      return (await api.migrateSubmit(base64)).signature;
    default:
      if (feePayer === wallet.address) return sendSignedTransaction(signed);
      return (await api.relaySignAndSend(base64)).signature;
  }
}
